import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { components } from '@luxuryescapes/contract-svc-promo/generated';
import { isValidTreatmentReferenceId } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import { getPromoDisplayConfigsInternal } from '~/services/PromoService';

type PromoBrand = components['schemas']['Promo Brands'];
export type PromoDisplayConfig = components['schemas']['PromoDisplayConfig'];

/** Enough results to pick from without paging; a narrower search finds the rest. */
const SEARCH_PAGE_SIZE = 20;

export const promoDisplayConfigQueryKeys = {
  all: ['promo-display-configs'] as const,
  searches: () => [...promoDisplayConfigQueryKeys.all, 'search'] as const,
  search: (brand: PromoBrand, search: string) => [...promoDisplayConfigQueryKeys.searches(), brand, search] as const,
  activeDetails: () => [...promoDisplayConfigQueryKeys.all, 'active-detail'] as const,
  activeDetail: (brand: PromoBrand, id: string) => [...promoDisplayConfigQueryKeys.activeDetails(), brand, id] as const,
};

type SearchParams = {
  brand: PromoBrand;
  /** Part of the promo code, campaign, url path or header text, or the start of the config id. */
  search: string;
  enabled?: boolean;
};

/** The brand's active promo display configs matching `search`, newest first. */
export function useGetPromoDisplayConfigs({ brand, search, enabled = true }: SearchParams) {
  const term = search.trim();
  return useQuery({
    queryKey: promoDisplayConfigQueryKeys.search(brand, term),
    queryFn: async ({ signal }) => {
      const response = await getPromoDisplayConfigsInternal(
        { brand, status: 'active', q: term || undefined, page: 0, pageSize: SEARCH_PAGE_SIZE },
        { signal },
      );
      return response.result.promoDisplayConfigs;
    },
    enabled: enabled && Boolean(brand),
    // Keep the last results on screen while the next search loads.
    placeholderData: keepPreviousData,
  });
}

type ActiveConfigParams = {
  brand: PromoBrand;
  promoDisplayConfigId: string;
};

/**
 * The config with this id if it is active for the brand, else `null`. This is
 * the same test svc-promo applies when a router is saved, so a `null` here is
 * an id the save will reject.
 *
 * Runs only for a well-formed id: the lookup compares against a uuid column, so
 * anything else is answered locally as invalid.
 */
export function useGetActivePromoDisplayConfig({ brand, promoDisplayConfigId }: ActiveConfigParams) {
  return useQuery({
    queryKey: promoDisplayConfigQueryKeys.activeDetail(brand, promoDisplayConfigId),
    queryFn: async ({ signal }) => {
      const response = await getPromoDisplayConfigsInternal(
        { brand, status: 'active', promo_display_config_id: promoDisplayConfigId, page: 0, pageSize: 1 },
        { signal },
      );
      return response.result.promoDisplayConfigs[0] ?? null;
    },
    enabled: Boolean(brand) && isValidTreatmentReferenceId(promoDisplayConfigId),
  });
}
